import React, { useState, useEffect, useRef } from 'react';
import './MiningRigMenu.scss';
import { updateMiningRigs } from '../../backend/services/gameData';
import { formatTokenAmount } from '../../utils/formatTokenAmount';

// Optimized Mining Rig Menu Component with Tiered Mining System
const MiningRigMenu = React.memo(({ onClose, gameData, updateGameData, refreshMoneyDisplay }) => {
  // Game state from props
  const [localMoney, setLocalMoney] = useState(() => gameData?.money || 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPausedInfo, setShowPausedInfo] = useState(false); // State for tooltip/modal
  
  // Get mining rigs array from game data or initialize
  const [miningRigs, setMiningRigs] = useState(() => {
    return Array.isArray(gameData?.data?.miningRigs) ? gameData.data.miningRigs : [];
  });
  
  // Use constants from gameData or fallback
  const gameConstants = gameData?.constants || {};
  const maxRigs = gameConstants.MAX_RIGS || 4;
  
  // Get tier values from constants or use fallbacks
  const rigTierPowers = gameConstants.RIG_TIER_POWERS || [10, 15, 25, 40];
  const rigTierCosts = gameConstants.RIG_TIER_COSTS || [10, 20, 40, 80];
  const rigTierEfficiency = gameConstants.RIG_TIER_EFFICIENCY || [1, 1.2, 1.5, 2];
  const dailyTokenPool = gameConstants.DAILY_TOKEN_POOL || 40;
  const rewardTokenSymbol = gameConstants.REWARD_TOKEN_SYMBOL || 'DCKSLAP';
  const rewardTokenDisplay = gameConstants.REWARD_TOKEN_DISPLAY || '$DCKSLAP';
  const miningRequirementTokenSymbol = gameConstants.MINING_REQUIREMENT_TOKEN_SYMBOL || '$REDDICKS';
  const miningIntervalsPerDay = gameConstants.MINING_INTERVALS_PER_DAY || 288;
  const minWalletBalance = gameConstants.MIN_WALLET_BALANCE_FOR_MINING || 15000; // Get the constant
  
  // Define tier names (these are UI-only and not needed in backend constants)
  const tierNames = ["Starter Rig", "Standard Rig", "Advanced Rig", "Enterprise Rig"];
  
  // Get user's mining power from save data
  const userMiningPower = gameData?.mining_power || 0;
  const totalMiningPower = Number(gameConstants.TOTAL_MINING_POWER);
  
  let tokensPerHour = 0;
  let isProductionRatePending = false;
  if (Number.isFinite(totalMiningPower) && totalMiningPower > 0 && userMiningPower > 0) {
    tokensPerHour = (dailyTokenPool / 24) * (userMiningPower / totalMiningPower);
  } else if (userMiningPower > 0) {
    isProductionRatePending = true;
  }
  
  // Flag to avoid re-renders when updating gameData
  const isUpdatingRef = useRef(false);
  
  // Update local state when gameData changes
  useEffect(() => {
    if (!isUpdatingRef.current && gameData?.data) {
      if (Array.isArray(gameData.data.miningRigs)) {
        setMiningRigs(gameData.data.miningRigs);
      }
      
      setLocalMoney(gameData.money || 0);
    }
  }, [gameData]);
  
  // Extract has_redicks status from gameData
  const hasRedicks = gameData?.has_redicks === true; // Default to false if undefined
  
  // Prevent events from propagating to canvas
  const handleMenuClick = (e) => {
    e.stopPropagation();
  };
  
  const handleMiningStatusClick = (e) => {
    e.stopPropagation();
    if (!hasRedicks) {
      setShowPausedInfo(true);
    }
  };
  
  const closePausedInfo = (e) => {
    e.stopPropagation();
    setShowPausedInfo(false);
  };
  
  const handleBuyRig = async (tier) => {
    // Check if already processing
    if (isProcessing) {
      return;
    }

    // Validate purchase conditions
    if (localMoney < rigTierCosts[tier-1]) {
      console.log(`Cannot purchase tier ${tier} rig. Money: ${localMoney}, Cost: ${rigTierCosts[tier-1]}`);
      return;
    }

    // Exit pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    
    // Set processing state
    setIsProcessing(true);
    
    // Calculate new values
    const newRig = {
      tier,
      efficiencyMultiplier: rigTierEfficiency[tier-1],
      isActive: true,
      totalMined: 0
    };
    
    const updatedRigs = [...miningRigs, newRig];
    const updatedMoney = localMoney - rigTierCosts[tier-1];
    
    // Set the updating flag
    isUpdatingRef.current = true;
    
    try {
      // Get the save ID from gameData
      const saveId = gameData?.id;
      
      if (!saveId) {
        throw new Error("Invalid save ID");
      }

      // Calculate new total mining power
      const newTotalPower = updatedRigs.reduce((total, rig) => {
        if (rig.isActive) {
          return total + rigTierPowers[rig.tier - 1];
        }
        return total;
      }, 0);

      // Call backend to update mining rigs
      const { success, save, error, constants } = await updateMiningRigs(
        saveId,
        updatedRigs,
        updatedMoney,
        newTotalPower
      );
      
      if (error) {
        console.error("Error updating mining rigs:", error);
        if (error.rateLimit) {
          const errorMessage = document.createElement('div');
          errorMessage.className = 'rate-limit-error';
          errorMessage.textContent = `Please wait before making another purchase (${error.retryAfter}s)`;
          document.body.appendChild(errorMessage);
          
          setTimeout(() => {
            if (errorMessage.parentNode) {
              errorMessage.parentNode.removeChild(errorMessage);
            }
          }, error.retryAfter * 1000);
        }
      } else if (success && save) {
        // Server confirmed transaction was valid
        setMiningRigs(save.data.miningRigs || []);
        setLocalMoney(save.money || updatedMoney);
        
        // Update parent's gameData
        if (updateGameData) {
          const updatedGameDataObj = {
            ...gameData,
            data: {
              ...gameData.data,
              miningRigs: save.data.miningRigs,
              energy: save.data.energy || 100
            },
            money: save.money || updatedMoney,
            mining_power: save.mining_power || newTotalPower,
            constants: constants || gameData.constants
          };
          
          updateGameData(updatedGameDataObj);
        } else if (refreshMoneyDisplay) {
          refreshMoneyDisplay(save.money || updatedMoney);
        }
      }
    } catch (error) {
      console.error("Error purchasing mining rig:", error);
    } finally {
      isUpdatingRef.current = false;
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="mining-rig-menu" onClick={handleMenuClick}>
      <div className="mining-rig-content">
        <button className="close-button" onClick={onClose} aria-label="Close">x</button>
        <h2>Mining Rigs</h2>
        
        <div className="mining-stats">
          <div className="stat">
            <span className="stat-label">{rewardTokenSymbol} Balance:</span>
            <span className="stat-value">{formatTokenAmount(localMoney)} {rewardTokenSymbol}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Mining Power:</span>
            <span className="stat-value">{userMiningPower} Power</span>
          </div>
          <div className="stat">
            <span className="stat-label">Production Rate:</span>
            <span className="stat-value">
              {isProductionRatePending ? 'Syncing...' : `${tokensPerHour.toFixed(4)} ${rewardTokenSymbol}/hr`}
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Energy:</span>
            <span className="stat-value">
              <div className="energy-bar">
                <div className="energy-fill" style={{ width: `100%` }}></div>
              </div>
              <span className="energy-text">
                Unlimited/100
                <span className="energy-info-icon" title="Energy system is currently in development">i</span>
              </span>
            </span>
          </div>
          {/* Mining Status Indicator */}
          <div className="stat mining-status-stat">
            <span className="stat-label">Mining Status:</span>
            {hasRedicks ? (
              <span className="stat-value mining-active">ACTIVE MINING</span>
            ) : (
              <button className="stat-value mining-paused" onClick={handleMiningStatusClick}>
                MINING PAUSED
                <span className="info-icon">?</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="mining-rigs-grid">
          {[...Array(maxRigs)].map((_, index) => {
            const tier = index + 1;
            const isOwned = miningRigs.some(rig => rig.tier === tier);
            const canAfford = localMoney >= rigTierCosts[index];
            const rigPower = rigTierPowers[index];
            
            return (
              <div key={index} className={`mining-rig-item ${isOwned ? 'owned' : 'available'}`}>
                <div className="rig-container">
                  <div className={`rig-icon tier-${tier}`}>
                    {isOwned ? (
                      <div className="owned-rig">
                        <div className="rig-lights"></div>
                        <div className="rig-body"></div>
                      </div>
                    ) : (
                      <div className="empty-rig">
                        <div className="rig-outline"></div>
                      </div>
                    )}
                  </div>
                  <div className="rig-info">
                    <span className="rig-number">{tierNames[index]}</span>
                    <span className="rig-status">
                      {isOwned ? 
                        (miningRigs.find(rig => rig.tier === tier)?.isActive ? 'Active' : 'Inactive') : 
                        'Not Purchased'}
                    </span>
                    {isOwned ? (
                      <>
                        <span className="rig-production">+{rigPower} Mining Power</span>
                        <span className="rig-efficiency">Efficiency: {rigTierEfficiency[index]}x</span>
                      </>
                    ) : (
                      <>
                        <span className="rig-production">+{rigPower} Mining Power</span>
                        <span className="rig-price">Price: {formatTokenAmount(rigTierCosts[index])} {rewardTokenSymbol}</span>
                      </>
                    )}
                  </div>
                  {!isOwned && (
                    <button 
                      className={`buy-rig-button ${!canAfford || isProcessing ? 'cannot-afford' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyRig(tier);
                      }}
                      disabled={!canAfford || isProcessing}
                    >
                      {isProcessing ? 
                        'Processing...' : 
                        !canAfford ? 
                          `Need ${formatTokenAmount(rigTierCosts[index] - localMoney)} ${rewardTokenSymbol}` : 
                          `Buy for ${formatTokenAmount(rigTierCosts[index])} ${rewardTokenSymbol}`
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mining-info">
          <p>Mining Power determines your share of the daily token pool ({formatTokenAmount(dailyTokenPool)} {rewardTokenSymbol})</p>
          <p>The scheduled reward job runs every {Math.round(1440 / miningIntervalsPerDay)} minutes and credits eligible miners proportionally.</p>
          <p>
            {isProductionRatePending
              ? 'Your production estimate will appear after the next network mining sync.'
              : `Your mining rigs currently estimate ${tokensPerHour.toFixed(4)} ${rewardTokenSymbol} per hour.`}
          </p>
        </div>
      </div>

      {/* Tooltip/Modal for paused mining */}
      {showPausedInfo && (
        <div className="mining-paused-tooltip" onClick={(e) => e.stopPropagation()}> {/* Prevent closing when clicking inside */}
          <div className="tooltip-content">
            <h3>Mining Paused</h3>
            <p>
              To be eligible for mining rewards, you must hold at least
              <strong> {formatTokenAmount(minWalletBalance)} {miningRequirementTokenSymbol}</strong> in your connected wallet.
            </p>
            <p>
              Eligible wallets receive {rewardTokenDisplay} payouts. The holder gate remains tied to {miningRequirementTokenSymbol}.
            </p>
            <button className="tooltip-close-button" onClick={closePausedInfo}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
});

MiningRigMenu.displayName = 'MiningRigMenu';

export default MiningRigMenu; 
